import { Autocomplete, Card, CardActions, TextField, Typography } from "@mui/material";
import styled from "styled-components";
import { ErrorRecord, Product } from "../../types";
import { useState, useEffect } from "react";
import { Building2, ChevronDown } from "lucide-react";

export type Props = {
    product: Product;
    handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    error: boolean;
    errors: ErrorRecord<Product>;
};

export const FactoryForm = (props: Props) => {
    const [buildingValue, setBuildingValue] = useState(props.product.warehousePlacement);
    const [roomValue, setRoomValue] = useState(props.product.roomPlacement);
    const [roomList, setRoomList] = useState<string[]>([]);
    const [townValue, setTownValue] = useState(props.product.town);

    useEffect(() => {
        const savedBuildingValue = localStorage.getItem("buildingValue");
        if (savedBuildingValue !== null) {
            setBuildingValue(savedBuildingValue);
            props.product.warehousePlacement = savedBuildingValue;
        }

        const savedTownValue = localStorage.getItem("townValue");
        if (savedTownValue !== null) {
            setTownValue(savedTownValue);
            props.product.town = savedTownValue;
        }

        const savedRoomList = localStorage.getItem("roomList");
        if (savedRoomList !== null) {
            const parsedRoomList: string[] = JSON.parse(savedRoomList) as string[];
            setRoomList(parsedRoomList);
            const savedRoom = localStorage.getItem("roomValue");
            if (savedRoom !== null) {
                setRoomValue(savedRoom);
                props.product.roomPlacement = savedRoom;
            }
        }
    }, []);

    const handleBuildingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setBuildingValue(newValue);
        localStorage.setItem("buildingValue", newValue);
        props.handleChange(e);
        setRoomList([]);
        localStorage.removeItem("roomList");
        setRoomValue("");
        localStorage.removeItem("roomValue");
    };

    const handleTownChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setTownValue(newValue);
        localStorage.setItem("townValue", newValue);
        props.handleChange(e);
    };

    const handleRoomChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRoomValue(e.target.value);
        localStorage.setItem("roomValue", e.target.value);
        props.handleChange(e);
    };
    const handleRoomBlur = () => {
        const newRoom = roomValue?.trim();
        if (newRoom && !roomList.includes(newRoom)) {
            const updatedRoomList = [newRoom, ...roomList];
            setRoomList(updatedRoomList);
            localStorage.setItem("roomList", JSON.stringify(updatedRoomList));
        }
    };

    const handleRoomSelect = (
        event: React.SyntheticEvent<Element, Event>,
        newValue: string | null,
    ) => {
        if (newValue) {
            setRoomValue(newValue);
            props.product.roomPlacement = newValue;
            localStorage.setItem("roomValue", newValue);
        }
    };

    return (
        <Container>
            <Card
                variant="outlined"
                sx={{
                    borderRadius: "30px",
                    width: "90%",
                    overflowY: "auto",
                    maxHeight: "80vh",
                    padding: "5px",
                    marginBottom: "30px",
                }}
            >
                {" "}
                <StyledCardActions>
                    <IconActionsWrapper>
                        <Building2 />{" "}
                        <Typography
                            fontFamily={"'Poppins', sans-serif"}
                            fontSize={18}
                            color="#8c8c8c"
                            fontWeight={600}
                        >
                            Location
                        </Typography>
                    </IconActionsWrapper>
                </StyledCardActions>
                <Form id="partial-submit-form" autoComplete="off">
                    <TextField
                        label="Town"
                        name="town"
                        variant="outlined"
                        value={townValue}
                        onChange={handleTownChange}
                        sx={{ ...sx, marginTop: "20px" }}
                    />
                    <TextField
                        label="Building"
                        name="warehousePlacement"
                        variant="outlined"
                        value={buildingValue}
                        onChange={handleBuildingChange}
                        sx={sx}
                    />
                    <Autocomplete
                        freeSolo
                        disableClearable
                        options={roomList}
                        popupIcon={<ChevronDown size={16} />}
                        sx={sx}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Room"
                                name="roomPlacement"
                                variant="outlined"
                                value={roomValue}
                                onChange={handleRoomChange}
                                onBlur={handleRoomBlur}
                                sx={sx}
                            />
                        )}
                        value={roomValue}
                        onChange={handleRoomSelect}
                    />
                    <TextField
                        label="Quantity"
                        name="quantity"
                        variant="outlined"
                        type="number"
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                        value={props.product.quantity}
                        onChange={e => props.handleChange(e)}
                        error={props.errors.quantity ? true : false}
                        helperText={props.errors.quantity}
                        sx={{ ...sx, marginBottom: "20px" }}
                    />
                </Form>
            </Card>
        </Container>
    );
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    align-content: center;
    width: 95%;
    margin: 0 auto;
    gap: 20px;
`;
const IconActionsWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: center;
    gap: 5px;
`;
const StyledCardActions = styled(CardActions)`
    display: flex;
    width: 100%;
    font-size: 3rem;
    border-bottom: 1px solid #ebebeb;
    //background-color: #ebebeb;
    align-items: center; /* Centrerar vertikalt i flex-container */
`;
const Container = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
`;

const sx = {
    "& .MuiInputBase-root": {
        fontFamily: "'Yllw Sans a1 241219', sans-serif",
        fontSize: "16px",
        fontWeight: "400",
        color: "black",
        backgroundColor: "white",
    },
    "& .MuiInputLabel-root": {
        fontFamily: "'Yllw Sans a1 241219', sans-serif",
        fontSize: "16px",
        fontWeight: "400",
        color: "rgba(0, 0, 0, 0.50)",
    },
    "& .MuiInputBase-input::placeholder": {
        fontFamily: "'Yllw Sans a1 241219', sans-serif",
        fontSize: "16px",
        fontWeight: "400",
        color: "rgba(0, 0, 0, 0.50)",
    },
    // marginBottom: "20px",
};
